import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SplitPanelState {
    content: React.ReactNode | null;
    header: string;
    isOpen: boolean;
}

interface SplitPanelContextType {
    state: SplitPanelState;
    openPanel: (header: string, content: React.ReactNode) => void;
    closePanel: () => void;
}

const SplitPanelContext = createContext<SplitPanelContextType>({
    state: { content: null, header: '', isOpen: false },
    openPanel: () => {},
    closePanel: () => {},
});

export const useSplitPanel = () => useContext(SplitPanelContext);

export const SplitPanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = useState<SplitPanelState>({ content: null, header: '', isOpen: false });
    const location = useLocation();

    const openPanel = useCallback((header: string, content: React.ReactNode) => {
        setState({ content, header, isOpen: true });
    }, []);

    const closePanel = useCallback(() => {
        setState(prev => ({ ...prev, isOpen: false }));
    }, []);

    useEffect(() => {
        closePanel();
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <SplitPanelContext.Provider value={{ state, openPanel, closePanel }}>
            {children}
        </SplitPanelContext.Provider>
    );
};
